import React, { useEffect, useRef, useState } from "react";
import {
  Input,
  InputGroup,
  InputGroupText,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
} from "reactstrap";
import "./Filter.scss";

export type FilterProps = {
  value?: string;
  onSearch?: (value: string) => void;
  onChange?: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  filterContent?: React.ReactNode;
  renderFilter?: (opts: { close: () => void }) => React.ReactNode;
  onApply?: (payload?: any) => void;
  onClear?: () => void;
  showClear?: boolean;
  className?: string;
  size?: "sm" | "md" | "lg";
};

const Filter: React.FC<FilterProps> = ({
  value = "",
  onSearch,
  onChange,
  placeholder = "Search...",
  debounceMs = 300,
  filterContent,
  renderFilter,
  onApply,
  onClear,
  showClear = true,
  className = "",
  size = "md",
}) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState(value);
  const timeout = useRef<number | null>(null);

  useEffect(() => {
    setInput(value);
  }, [value]);

  useEffect(() => {
    if (timeout.current) window.clearTimeout(timeout.current);
    timeout.current = window.setTimeout(() => {
      onSearch?.(input);
    }, debounceMs);

    return () => {
      if (timeout.current) window.clearTimeout(timeout.current);
    };
  }, [input, debounceMs, onSearch]);

  const handleChange = (v: string) => {
    setInput(v);
    onChange?.(v);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (timeout.current) window.clearTimeout(timeout.current);
      onSearch?.(input);
    }
  };

  const handleApply = (payload?: any) => {
    onApply?.(payload);
    setOpen(false);
  };

  const handleClear = () => {
    setInput("");
    onChange?.("");
    onClear?.();
    onSearch?.("");
  };

  return (
    <div
      className={["cg-filter", `cg-filter--${size}`, className]
        .filter(Boolean)
        .join(" ")}
    >
      <InputGroup>
        <InputGroupText className="cg-filter__icon-left">
          <i className="ri-search-line" />
        </InputGroupText>
        <Input
          placeholder={placeholder}
          value={input}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        {showClear && input ? (
          <InputGroupText
            className="cg-filter__clear"
            onClick={handleClear}
            style={{ cursor: "pointer" }}
          >
            <i className="ri-close-line" />
          </InputGroupText>
        ) : null}

        <Dropdown isOpen={open} toggle={() => setOpen((s) => !s)}>
          <DropdownToggle className="cg-filter__filter-btn" caret tag="div">
            <Button color="link" className="p-0">
              <i className="ri-equalizer-line" style={{ fontSize: "1.1rem" }} />
            </Button>
          </DropdownToggle>
          <DropdownMenu right className="cg-filter__menu">
            {renderFilter ? (
              renderFilter({ close: () => setOpen(false) })
            ) : (
              <div className="cg-filter__menu-content">{filterContent}</div>
            )}
            <div className="cg-filter__menu-actions">
              <Button
                color="secondary"
                size="sm"
                onClick={() => {
                  setOpen(false);
                  onClear?.();
                }}
              >
                Reset
              </Button>
              <Button color="primary" size="sm" onClick={() => handleApply()}>
                Apply
              </Button>
            </div>
          </DropdownMenu>
        </Dropdown>
      </InputGroup>
    </div>
  );
};

export default Filter;
